<template>
  <div v-if="!!option" id="preorder-form" class="preorder-workspace">
    <b-card no-body class="preorder-toolbar mb-2">
      <b-card-body class="preorder-toolbar-body">
        <div class="preorder-toolbar-content">
          <div class="preorder-toolbar-main">
            <div class="preorder-toolbar-icon">
              <feather-icon icon="ClipboardIcon" size="24" />
            </div>
            <div>
              <small class="preorder-toolbar-eyebrow">
                {{ $t(label("preorderWorkspace")) }}
              </small>
              <h2 class="mb-25">{{ $t(label("managePreorder")) }}</h2>
              <div class="preorder-toolbar-meta">
                <span>#{{ preorder.idOrder || "-" }}</span>
                <span v-if="preorder.nameClient">{{
                  preorder.nameClient
                }}</span>
                <span>{{ dateFormat(preorder.date) }}</span>
              </div>
            </div>
          </div>

          <div class="preorder-toolbar-actions">
            <b-badge
              pill
              class="preorder-status-badge"
              :variant="statusVariant(preorder.nameStatus)"
            >
              {{ preorder.nameStatus || "-" }}
            </b-badge>
            <b-button variant="outline-secondary" @click="goBack">
              <feather-icon icon="ArrowLeftIcon" class="mr-50" />
              {{ $t("back") }}
            </b-button>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <div class="preorder-content">
      <validation-observer ref="simpleRules">
        <b-form>
          <b-row>
            <b-col cols="12" md="6">
              <b-card
                no-body
                class="preorder-section-card customer-section-card"
              >
                <b-card-header class="preorder-section-header">
                  <div class="section-heading">
                    <b-avatar
                      :text="customerInitials"
                      variant="light-primary"
                      size="42"
                    />
                    <div>
                      <small class="text-muted">{{
                        $t(label("customer"))
                      }}</small>
                      <h4 class="mb-25">{{ preorder.nameClient || "-" }}</h4>
                      <small class="text-muted">{{
                        $t(label("customerReviewHelp"))
                      }}</small>
                    </div>
                  </div>
                </b-card-header>
                <b-card-body class="preorder-section-body info-list">
                  <b-card-text class="info-row">
                    <feather-icon
                      icon="MailIcon"
                      size="21"
                      class="info-row-icon text-info"
                    />
                    <span class="info-row-copy">
                      <small>{{ $t(label("email")) }}</small>
                      <strong>{{ preorder.emailClient || "-" }}</strong>
                    </span>
                  </b-card-text>
                  <b-card-text class="info-row">
                    <feather-icon
                      icon="SmartphoneIcon"
                      size="21"
                      class="info-row-icon"
                    />
                    <span class="info-row-copy">
                      <small>{{ $t(label("phone")) }}</small>
                      <strong>{{ preorder.phoneClient || "-" }}</strong>
                    </span>
                  </b-card-text>
                  <b-card-text class="info-row">
                    <feather-icon
                      icon="HomeIcon"
                      size="21"
                      class="info-row-icon text-success"
                    />
                    <span class="info-row-copy">
                      <small>{{ $t(label("mainAddress")) }}</small>
                      <strong>{{ preorder.addressClient || "-" }}</strong>
                    </span>
                  </b-card-text>
                  <b-card-text class="info-row shipping-info-row mb-0">
                    <feather-icon
                      icon="MapPinIcon"
                      size="21"
                      class="info-row-icon text-danger"
                    />
                    <span class="info-row-copy">
                      <small>{{ $t(label("shippingInformation")) }}</small>
                      <strong>{{
                        preorder.shippingAddressClient ||
                        preorder.shippingAddress ||
                        "-"
                      }}</strong>
                    </span>
                  </b-card-text>
                </b-card-body>
              </b-card>
            </b-col>

            <b-col cols="12" md="6">
              <b-card
                no-body
                class="preorder-section-card financial-section-card"
              >
                <b-card-header class="preorder-section-header">
                  <div class="section-heading">
                    <div class="section-heading-icon financial-icon">
                      <feather-icon icon="DollarSignIcon" size="19" />
                    </div>
                    <div>
                      <h4 class="mb-25">{{ $t(label("financialSummary")) }}</h4>
                      <small class="text-muted">{{
                        $t(label("financialSummaryHelp"))
                      }}</small>
                    </div>
                  </div>
                </b-card-header>
                <b-card-body class="preorder-section-body">
                  <div class="summary-row">
                    <span class="text-muted">{{ $t(label("subtotal")) }}</span>
                    <strong
                      >{{ formatAmount(primarySubtotal) }} {{ divisa }}</strong
                    >
                  </div>
                  <div class="summary-row">
                    <span class="text-muted">
                      {{ $t(label("iva"), { rate: formatRate(ivaRate) }) }}
                    </span>
                    <strong>{{ formatAmount(ivaAmount) }} {{ divisa }}</strong>
                  </div>
                  <div class="summary-row summary-row--highlight">
                    <span>{{ $t(label("totalWithIva")) }}</span>
                    <strong
                      >{{ formatAmount(totalWithIva) }} {{ divisa }}</strong
                    >
                  </div>
                  <div class="financial-divider"></div>
                  <div class="summary-row">
                    <span class="text-muted">{{
                      $t(label("subtotalInBolivars"))
                    }}</span>
                    <strong>{{ formatAmount(bolivarSubtotal) }} Bs</strong>
                  </div>
                  <div class="summary-row">
                    <span class="text-muted">
                      {{
                        $t(label("ivaInBolivars"), {
                          rate: formatRate(ivaRate),
                        })
                      }}
                    </span>
                    <strong>{{ formatAmount(bolivarIvaAmount) }} Bs</strong>
                  </div>
                  <div class="summary-row summary-row--highlight mb-0">
                    <span>{{ $t(label("totalInBolivars")) }}</span>
                    <strong>{{ formatAmount(totalInBolivars) }} Bs</strong>
                  </div>
                  <div class="summary-context">
                    <feather-icon icon="InfoIcon" size="16" />
                    <span>{{ $t(label("financialOfficialHelp")) }}</span>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>

            <!-- <b-col cols="12">
              <b-card :title="$t(label('shippingInformation'))">
                <div v-if="shippingDetails.length">
                  <b-row>
                    <b-col
                      v-for="(detail, index) in shippingDetails"
                      :key="`shipping-${index}`"
                      cols="12"
                      md="6"
                      class="mb-50"
                    >
                      <span>{{ detail }}</span>
                    </b-col>
                  </b-row>
                </div>
                <p v-else class="mb-0">-</p>
              </b-card>
            </b-col> -->

            <b-col v-if="canAssignEmployees" cols="12" md="6">
              <b-card
                no-body
                class="preorder-section-card assignment-section-card"
              >
                <b-card-header class="preorder-section-header">
                  <div class="section-heading">
                    <div class="section-heading-icon assignment-icon">
                      <feather-icon icon="UserCheckIcon" size="19" />
                    </div>
                    <div>
                      <h4 class="mb-25">{{ $t(label("employeeToAssign")) }}</h4>
                      <small class="text-muted">{{
                        $t(label("employeeAssignmentHelp"))
                      }}</small>
                    </div>
                  </div>
                </b-card-header>
                <b-card-body class="preorder-section-body">
                  <label class="d-inline-block text-sm-left mr-50">
                    {{ $t(label("employee")) }}
                  </label>
                  <v-select
                    v-model="selectedEmployee"
                    ref="employeeSelect"
                    :options="employees"
                    :reduce="(option) => option.idEmployee"
                    label="name"
                    :placeholder="$t('select')"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :loading="employeeLoading"
                    :filterable="false"
                    @search="onSearchEmployees"
                    @open="onOpenEmployees"
                    @close="onCloseEmployees"
                  >
                    <template #list-footer>
                      <li
                        v-show="employeeHasNext || employeeLoading"
                        ref="loadEmployees"
                        class="employee-loader"
                      >
                        {{ $t("loadingOption") }}
                      </li>
                    </template>
                    <span slot="no-options">{{ $t("sorryOption") }}</span>
                  </v-select>
                  <small class="d-block text-muted mt-75">
                    {{ $t(label("employeeRequiredHelp")) }}
                  </small>
                </b-card-body>
              </b-card>
            </b-col>

            <!-- <b-col cols="12" md="6">
              <b-card no-body class="preorder-section-card payment-section-card">
                <b-card-header class="preorder-section-header">
                  <div class="section-heading">
                    <div class="section-heading-icon payment-icon">
                      <feather-icon icon="CreditCardIcon" size="19" />
                    </div>
                    <div>
                      <h4 class="mb-25">{{ $t(label("paymentInformation")) }}</h4>
                      <small class="text-muted">{{ $t(label("paymentReviewHelp")) }}</small>
                    </div>
                  </div>
                </b-card-header>
                <b-card-body class="preorder-section-body">
                <div class="summary-row">
                  <span class="text-muted">{{
                    $t(label("type_payment"))
                  }}</span>
                  <strong>{{ preorder.nameTypePaymentMethods || "-" }}</strong>
                </div>
                <div class="summary-row">
                  <span class="text-muted">{{
                    $t(label("paymentMethod"))
                  }}</span>
                  <strong>{{
                    preorder.informationPaymentMethods || "-"
                  }}</strong>
                </div>
                <div class="summary-row mb-0">
                  <span class="text-muted">{{
                    $t(label("paymentRecords"))
                  }}</span>
                  <strong>{{ preorder.payment.length }}</strong>
                </div>
                </b-card-body>
              </b-card>
            </b-col> -->

            <b-col cols="12">
              <b-card
                no-body
                class="preorder-section-card products-section-card"
              >
                <b-card-header class="preorder-section-header">
                  <div class="section-heading">
                    <div class="section-heading-icon products-icon">
                      <feather-icon icon="PackageIcon" size="19" />
                    </div>
                    <div>
                      <h4 class="mb-25">{{ $t(label("products")) }}</h4>
                      <small class="text-muted">{{
                        $t(label("productsReviewHelp"))
                      }}</small>
                    </div>
                  </div>
                </b-card-header>
                <b-card-body class="preorder-section-body pt-0">
                  <b-table
                    id="preordersform-table"
                    small
                    responsive
                    :head-variant="headVariant"
                    :striped="striped"
                    :bordered="bordered"
                    :borderless="borderless"
                    :outlined="outlined"
                    :items="preorder.products"
                    :fields="productFields"
                    show-empty
                  >
                    <template #head()="data">
                      <span class="text-black">{{
                        $t(labelHeader(data.label.toLowerCase()))
                      }}</span>
                    </template>
                    <template #cell(name)="data">
                      {{ data.item.name || data.item.product || "-" }}
                    </template>
                    <template #cell(quantity)="data">
                      {{ data.item.quantity || data.item.cant || 0 }}
                    </template>
                    <template #cell(price)="data">
                      {{ formatPrice(data.item) }}
                      {{ divisa }}
                    </template>
                    <template #empty>
                      <div class="text-center text-muted my-1">
                        {{ $t("notRegister") }}
                      </div>
                    </template>
                  </b-table>
                </b-card-body>
              </b-card>
            </b-col>

            <b-col cols="12" lg="4">
              <b-card no-body class="preorder-section-card notes-section-card">
                <b-card-header class="preorder-section-header">
                  <div class="section-heading">
                    <div class="section-heading-icon notes-icon">
                      <feather-icon icon="FileTextIcon" size="19" />
                    </div>
                    <h4 class="mb-0">{{ $t(label("notes")) }}</h4>
                  </div>
                </b-card-header>
                <b-card-body class="preorder-section-body">
                  <p class="notes-content mb-0">
                    {{ preorder.notes || $t(label("noNotes")) }}
                  </p>
                </b-card-body>
              </b-card>
            </b-col>

            <b-col cols="12" lg="8">
              <b-card
                no-body
                class="preorder-section-card history-section-card"
              >
                <b-card-header class="preorder-section-header">
                  <div class="section-heading">
                    <div class="section-heading-icon history-icon">
                      <feather-icon icon="ActivityIcon" size="19" />
                    </div>
                    <div>
                      <h4 class="mb-25">{{ $t(label("history_order")) }}</h4>
                      <small class="text-muted">{{
                        $t(label("historyReviewHelp"))
                      }}</small>
                    </div>
                  </div>
                  <b-badge pill variant="light-secondary">
                    {{ preorder.statusHistory.length }}
                  </b-badge>
                </b-card-header>
                <b-card-body class="preorder-section-body pt-0">
                  <b-table
                    small
                    responsive
                    :head-variant="headVariant"
                    :bordered="bordered"
                    :borderless="borderless"
                    :items="preorder.statusHistory"
                    :fields="statusHistoryFields"
                    show-empty
                  >
                    <template #head()="data">
                      <span class="text-black">{{
                        $t(labelHeader(data.label.toLowerCase()))
                      }}</span>
                    </template>
                    <template #cell(dateUpdate)="data">
                      {{ dateFormat(data.item.dateUpdate) }}
                    </template>
                    <template #cell(nameStatus)="data">
                      <b-badge :variant="statusVariant(data.item.nameStatus)">
                        {{ data.item.nameStatus || "-" }}
                      </b-badge>
                    </template>
                    <template #empty>
                      <div class="text-center text-muted my-1">
                        {{ $t("notRegister") }}
                      </div>
                    </template>
                  </b-table>
                </b-card-body>
              </b-card>
            </b-col>

            <b-col v-if="option != 2" class="decision-column" cols="12">
              <div class="decision-panel">
                <div class="decision-copy">
                  <div class="decision-icon">
                    <feather-icon icon="ShieldIcon" size="22" />
                  </div>
                  <div>
                    <h4 class="mb-25">{{ $t(label("decisionTitle")) }}</h4>
                    <small class="text-muted">{{
                      $t(label("decisionHelp"))
                    }}</small>
                  </div>
                </div>
                <div class="decision-actions">
                  <b-button
                    v-if="preorder.urlChatSofia"
                    class="mr-50 d-flex"
                    variant="outline-info"
                    aria-label="SOFIA"
                    title="SOFIA"
                    @click="onSOFIA"
                  >
                    <img
                      src="/log-sof-favicon.png"
                      alt=""
                      width="12"
                      height="12"
                      class="mr-50"
                    />
                    {{ $t(label("whatsAppSofia")) }}
                  </b-button>

                  <b-button
                    v-if="preorder.urlChatBoot"
                    variant="outline-success"
                    aria-label="WhatsApp"
                    title="WhatsApp"
                    @click="onWhatsApp"
                  >
                    <feather-icon icon="MessageCircleIcon" class="mr-50" />
                    {{ $t(label("whatsAppContact")) }}
                  </b-button>
                  <b-button
                    v-if="canAssignEmployees && idJobSales"
                    variant="outline-danger"
                    @click="declinePreOrder"
                  >
                    <feather-icon icon="XCircleIcon" class="mr-50" />
                    {{ $t(label("decline")) }}
                  </b-button>
                  <b-button
                    v-if="canAssignEmployees"
                    variant="primary"
                    @click="approvePreOrder"
                  >
                    <feather-icon icon="CheckCircleIcon" class="mr-50" />
                    {{ $t(label("approve")) }}
                  </b-button>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BCardTitle,
  BCardText,
  BRow,
  BCol,
  BButton,
  BTable,
  BForm,
  BBadge,
  BAvatar,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import {
  getPreOrder,
  preOrderApprove,
  preOrderDecline,
} from "@core/api/request/preOrders";
import { allEmployeesByCompany } from "@core/api/request/employees";
import { getCompany } from "@core/api/request/company";
import { getParamsAll, showLoading, stopLoading } from "@core/utils/utils";
import _ from "lodash";

const preorderTemplate = {
  idEmployee: null,
  addressClient: "",
  phoneClient: "",
  emailClient: "",
  idClient: null,
  nameClient: "",
  idOrder: null,
  notes: "",
  products: [],
  date: "",
  nameStatus: "",
  urlChatBoot: "",
  urlChatSofia: "",
  amount: null,
  amountVES: null,
  amountVESTasaConfigurada: null,
  totalToPay: null,
  totalToPayVES: null,
  totalToPayVESTasaConfigurada: null,
  nameTypePaymentMethods: "",
  informationPaymentMethods: "",
  shippingAddressClient: "",
  shippingAddress: "",
  delivery: null,
  payment: [],
  statusHistory: [],
  employees: [],
};

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BForm,
    BCard,
    BCardHeader,
    BCardBody,
    BCardTitle,
    BCardText,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BAvatar,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data: () => ({
    isSaveMode: true,
    preorder: { ...preorderTemplate },
    option: "",
    headVariant: "light",
    striped: false,
    outlined: false,
    bordered: true,
    borderless: false,
    productFields: [
      { label: "name", key: "name", sortable: false },
      { label: "quantity", key: "quantity", sortable: false },
      { label: "characteristics", key: "characteristics", sortable: false },
      { label: "price", key: "price", sortable: false },
      { label: "school", key: "nameColegio", sortable: false },
    ],
    statusHistoryFields: [
      { label: "date", key: "dateUpdate", sortable: false },
      { label: "active", key: "nameStatus", sortable: false },
    ],
    employees: [],
    selectedEmployee: null,
    assignedEmployeeOption: null,
    companyGuid: null,
    whatsappPhone: null, // Número de teléfono para WhatsApp
    loading: false,
    employeePage: 1,
    employeePageSize: 20,
    employeeHasNext: true,
    employeeLoading: false,
    employeeObserver: null,
    employeeSelectOpen: false,
    employeeFillingOptions: false,
    employeeSearch: "",
    employeeSearchTimer: null,
    employeeLoadedIds: [],
    employeeDropdownEl: null,
  }),
  computed: {
    customerInitials() {
      return String(this.preorder.nameClient || "")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase();
    },
    divisa() {
      return this.$store.state.appConfig.divisa || "USD";
    },
    ivaRate() {
      const rate = Number(this.$store.state.appConfig.iva);
      return Number.isFinite(rate) && rate >= 0 && rate <= 100 ? rate : 0;
    },
    primarySubtotal() {
      return this.toValidAmount(this.preorder.amount);
    },
    primaryTotal() {
      return this.firstValidAmount([
        this.preorder.totalToPay,
        this.preorder.amount,
      ]);
    },
    ivaAmount() {
      return this.primarySubtotal * (this.ivaRate / 100);
    },
    totalWithIva() {
      return this.primaryTotal + this.ivaAmount;
    },
    bolivarSubtotal() {
      return this.firstPositiveAmount([
        this.preorder.amountVESTasaConfigurada,
        this.preorder.amountVES,
      ]);
    },
    bolivarTotal() {
      return this.firstPositiveAmount([
        this.preorder.totalToPayVESTasaConfigurada,
        this.preorder.totalToPayVES,
        this.preorder.amountVESTasaConfigurada,
        this.preorder.amountVES,
      ]);
    },
    bolivarIvaAmount() {
      return this.bolivarSubtotal * (this.ivaRate / 100);
    },
    totalInBolivars() {
      return this.bolivarTotal + this.bolivarIvaAmount;
    },
    idOrder() {
      return this.$route.params.idOrder;
    },
    shippingDetails() {
      const value =
        this.preorder.shippingAddressClient ||
        this.preorder.shippingAddress ||
        "";
      return value
        .split("|")
        .map((item) => item.trim())
        .filter(Boolean);
    },
    isEmployee() {
      return this.$store.getters["appConfig/isEmployee"];
    },
    idEmployee() {
      return this.$store.getters["appConfig/idEmployee"];
    },
    idJobSales() {
      const role = this.$store.getters["appConfig/role"];
      return String(role || "")
        .trim()
        .toUpperCase() === "JEFEDEVENTAS";
    },
    canAssignEmployees() {
      return (
        (!this.isEmployee && this.idEmployee == null) || this.idJobSales
      );
    },
  },

  async created() {
    localize("es");
    this.initialize();
  },
  mounted() {
    if (typeof IntersectionObserver !== "undefined") {
      this.employeeObserver = new IntersectionObserver(
        this.infiniteScrollEmployees,
      );
    }
  },
  beforeDestroy() {
    clearTimeout(this.employeeSearchTimer);
    if (this.employeeObserver) this.employeeObserver.disconnect();
    this.detachEmployeeScroll();
  },
  methods: {
    label(field) {
      return `pages.preorders.form.label.${field}`;
    },
    labelHeader(field) {
      return `pages.preorders.table.tableHeader.${field}`;
    },
    getTitle() {
      if (this.option === 1) return "register";
      else if (this.option === 2) return "show";
      else if (this.option === 3) return "edit";
    },

    async initialize() {
      this.companyGuid =
        this.$store.getters["appConfig/effectiveCompanyGuid"] ||
        this.$store.state.appConfig.activeCompanyGuid;
      this.option = this.$route.params.option;

      if (this.option === undefined) {
        this.$router.push({ name: "preorders" }).catch(() => {});
      }

      switch (this.option) {
        case 1:
          this.onCancel();
          // this.employee.guidCompany = this.$store.state.appConfig.activeCompanyGuid;
          break;
        case 2:
        case 3:
          if (this.$route.params.guid === undefined) {
            this.$router.push({ name: "preorders" }).catch(() => {});
          }
          this.isSaveMode = this.option !== 2;

          await this.showPreOrder(this.$route.params.guid);
          this.resetEmployeePagination();
          await this.loadEmployees();
          await this.getCompanyGuid(this.companyGuid);
          break;
      }
    },

    async showPreOrder(guid) {
      showLoading();
      try {
        await getPreOrder(guid)
          .then((resp) => {
            const preOrder = _.get(resp, "data");
            const employeeAssignments = Array.isArray(preOrder.employees)
              ? preOrder.employees
              : [];
            this.assignedEmployeeOption =
              this.getAssignedEmployeeOption(employeeAssignments);
            this.selectedEmployee =
              this.assignedEmployeeOption?.idEmployee ?? null;
            this.preorder = Object.assign({}, this.preorder, {
              idEmployee: this.selectedEmployee,
              idOrder: preOrder.idOrder || null,
              idClient: preOrder.idClient || null,
              nameClient: preOrder.nameClient || "",
              emailClient: preOrder.emailClient || "",
              phoneClient: preOrder.phoneClient || "",
              addressClient: preOrder.addressClient || "",
              notes: preOrder.notes || "",
              date: preOrder.date || "",
              nameStatus: preOrder.nameStatus || "",
              amount: preOrder.amount ?? null,
              amountVES: preOrder.amountVES ?? null,
              amountVESTasaConfigurada:
                preOrder.amountVESTasaConfigurada ?? null,
              totalToPay: preOrder.totalToPay ?? null,
              totalToPayVES: preOrder.totalToPayVES ?? null,
              totalToPayVESTasaConfigurada:
                preOrder.totalToPayVESTasaConfigurada ?? null,
              nameTypePaymentMethods: preOrder.nameTypePaymentMethods || "",
              informationPaymentMethods:
                preOrder.informationPaymentMethods || "",
              shippingAddressClient: preOrder.shippingAddressClient || "",
              shippingAddress: preOrder.shippingAddress || "",
              delivery: preOrder.delivery ?? null,
              payment: Array.isArray(preOrder.payment) ? preOrder.payment : [],
              statusHistory: Array.isArray(preOrder.statusHistory)
                ? preOrder.statusHistory
                : [],
              employees: employeeAssignments,
              products: Array.isArray(preOrder.products)
                ? preOrder.products
                : [],
              urlChatBoot: preOrder.urlChatBoot || "",
              urlChatSofia: preOrder.urlChatSofia || "",
            });
            stopLoading();
          })
          .catch((error) => {
            console.log("error", error.message);
            stopLoading();
            this.alertToast(
              "danger",
              "Error",
              error.message,
              "AlertTriangleIcon",
            );
          });
      } catch (error) {
        console.log("error", error.message);
        stopLoading();
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
      }
    },

    formatPrice(row) {
      // Si priceByRange tiene el formato "precioDesde - precioHasta", mostrar rango
      if (row.priceByRange) {
        return this.$t(this.label("priceRange"), {
          from: this.formatAmount(row.priceFrom),
          to: this.formatAmount(row.priceTo),
        });
      }
      // Si no, mostrar como precio fijo formateado
      return this.formatAmount(row.price);
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === "") return "-";
      const amount = Number(value);
      if (Number.isNaN(amount)) return value;
      return amount.toLocaleString(this.$i18n.locale || "es-VE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatRate(value) {
      const rate = Number(value);
      return Number.isFinite(rate)
        ? rate.toLocaleString(this.$i18n.locale || "es-VE")
        : 0;
    },
    toValidAmount(value) {
      const amount = Number(value);
      return Number.isFinite(amount) ? amount : 0;
    },
    firstValidAmount(values) {
      const value = values.find(
        (item) =>
          item !== null &&
          item !== undefined &&
          item !== "" &&
          Number.isFinite(Number(item)),
      );
      return this.toValidAmount(value);
    },
    firstPositiveAmount(values) {
      const value = values.find((item) => {
        const amount = Number(item);
        return Number.isFinite(amount) && amount > 0;
      });
      return this.toValidAmount(value);
    },
    statusVariant(status) {
      switch (status) {
        case "Generada":
          return "secondary";
        case "Atendida":
          return "warning";
        case "Procesada":
          return "info";
        case "Terminada":
          return "success";
        case "Cancelada":
          return "danger";
        default:
          return "primary";
      }
    },

    resetEmployeePagination() {
      this.employeePage = 1;
      this.employeeHasNext = true;
      this.employeeLoadedIds = [];
      this.employees = this.assignedEmployeeOption
        ? [{ ...this.assignedEmployeeOption }]
        : [];
    },

    getAssignedEmployeeOption(employeeAssignments) {
      const assignment = employeeAssignments.find(
        (item) =>
          item?.idEmployee != null || item?.employee?.idEmployee != null,
      );
      if (!assignment) return null;

      const employee = assignment.employee || {};
      const idEmployee = assignment.idEmployee ?? employee.idEmployee;
      return {
        ...employee,
        idEmployee,
        name: employee.name || assignment.nameEmployee || String(idEmployee),
      };
    },

    async loadEmployees() {
      if (!this.companyGuid || this.employeeLoading) return;
      this.employeeLoading = true;
      const requestedSearch = this.employeeSearch;
      const arrParams = [
        `guidCompany,${this.companyGuid}`,
        `page,${this.employeePage}`,
        `rows,${this.employeePageSize}`,
        "active,true",
      ];
      if (requestedSearch) arrParams.push(`name,${requestedSearch}`);
      const param = getParamsAll(arrParams);
      try {
        const resp = await allEmployeesByCompany(param);
        if (requestedSearch !== this.employeeSearch) return;

        const payload = _.get(resp, "data") || {};
        const items = Array.isArray(payload.items) ? payload.items : [];

        const currentPage = Number(payload.currentPage || this.employeePage);
        const totalPages = Number(payload.totalPages || 0);
        const totalRows = Number(payload.totalRows || 0);

        const merged = [...this.employees];
        const existing = new Set(
          merged.map((employee) => String(employee.idEmployee)),
        );
        const loadedIds = new Set(this.employeeLoadedIds);
        items.forEach((item) => {
          const employeeId = String(item.idEmployee);
          loadedIds.add(employeeId);
          if (!existing.has(employeeId)) {
            merged.push(item);
            existing.add(employeeId);
          }
        });
        this.employees = merged;
        this.employeeLoadedIds = [...loadedIds];

        if (totalPages > 0) {
          this.employeeHasNext = currentPage < totalPages;
        } else if (totalRows > 0) {
          this.employeeHasNext =
            this.employeeLoadedIds.length < totalRows && items.length > 0;
        } else {
          this.employeeHasNext = items.length === this.employeePageSize;
        }
      } catch (error) {
        console.error("Error loading employees", error);
      } finally {
        this.employeeLoading = false;
        if (this.employeeSelectOpen && !this.employeeFillingOptions) {
          await this.observeEmployeeLoader();
        }
      }
    },
    async loadMoreEmployees() {
      if (!this.employeeHasNext || this.employeeLoading) return;
      this.employeePage += 1;
      await this.loadEmployees();
    },
    async onOpenEmployees() {
      this.employeeSelectOpen = true;
      await this.$nextTick();
      this.attachEmployeeScroll();
      await this.fillEmployeeDropdown();
      await this.observeEmployeeLoader();
    },
    onCloseEmployees() {
      this.employeeSelectOpen = false;
      if (this.employeeObserver) this.employeeObserver.disconnect();
      this.detachEmployeeScroll();
    },
    onSearchEmployees(value) {
      clearTimeout(this.employeeSearchTimer);
      this.employeeSearchTimer = setTimeout(async () => {
        this.employeeSearch = String(value || "").trim();
        this.resetEmployeePagination();
        await this.loadEmployees();
        if (this.employeeSelectOpen) {
          await this.fillEmployeeDropdown();
          await this.observeEmployeeLoader();
        }
      }, 350);
    },
    attachEmployeeScroll() {
      this.detachEmployeeScroll();
      const menu =
        this.$refs.employeeSelect?.$el?.querySelector(".vs__dropdown-menu");
      if (!menu) return;

      this.employeeDropdownEl = menu;
      menu.addEventListener("scroll", this.handleEmployeeDropdownScroll);
    },
    async fillEmployeeDropdown() {
      if (this.employeeFillingOptions || !this.employeeSelectOpen) return;

      this.employeeFillingOptions = true;
      try {
        await this.$nextTick();
        let menu = this.employeeDropdownEl;
        let attempts = 0;

        while (
          menu &&
          this.employeeSelectOpen &&
          this.employeeHasNext &&
          menu.scrollHeight <= menu.clientHeight + 1 &&
          attempts < 20
        ) {
          const previousCount = this.employeeLoadedIds.length;
          await this.loadMoreEmployees();
          await this.$nextTick();
          menu = this.employeeDropdownEl;
          attempts += 1;

          if (this.employeeLoadedIds.length === previousCount) break;
        }
      } finally {
        this.employeeFillingOptions = false;
      }
    },
    async observeEmployeeLoader() {
      if (
        !this.employeeSelectOpen ||
        !this.employeeObserver ||
        !this.employeeHasNext
      ) {
        return;
      }

      await this.$nextTick();
      const loader = Array.isArray(this.$refs.loadEmployees)
        ? this.$refs.loadEmployees[0]
        : this.$refs.loadEmployees;
      if (loader) {
        this.employeeObserver.disconnect();
        this.employeeObserver.observe(loader);
      }
    },
    async infiniteScrollEmployees([{ isIntersecting, target }]) {
      if (
        !isIntersecting ||
        this.employeeLoading ||
        this.employeeFillingOptions ||
        !this.employeeHasNext
      ) {
        return;
      }

      const list = target.offsetParent;
      const scrollTop = list ? list.scrollTop : 0;
      this.employeeObserver.disconnect();

      await this.loadMoreEmployees();
      await this.$nextTick();

      if (list) list.scrollTop = scrollTop;
    },
    detachEmployeeScroll() {
      if (this.employeeDropdownEl) {
        this.employeeDropdownEl.removeEventListener(
          "scroll",
          this.handleEmployeeDropdownScroll,
        );
        this.employeeDropdownEl = null;
      }
    },
    handleEmployeeDropdownScroll(event) {
      const target = event?.target;
      if (!target) return;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
        this.loadMoreEmployees();
      }
    },

    async approvePreOrder() {
      if (!this.selectedEmployee) {
        this.alertToast(
          "danger",
          this.$t("Alert"),
          this.$t(this.label("selectEmployee")),
        );
        return;
      }
      if (!this.preorder.idOrder) return;
      showLoading();
      try {
        await preOrderApprove(this.preorder.idOrder, this.selectedEmployee);
        this.alertToast(
          "success",
          this.$t("success"),
          this.$t(this.label("approveSuccess")),
        );
        this.$router.push({ name: "preorders" });
      } catch (error) {
        console.error("Error approving preorder", error);
        this.alertToast(
          "danger",
          this.$t("Alert"),
          error.data?.message || error.message,
        );
      } finally {
        stopLoading();
      }
    },

    async declinePreOrder() {
      if (!this.preorder.idOrder) return;
      showLoading();
      try {
        await preOrderDecline(this.preorder.idOrder);
        this.alertToast(
          "success",
          this.$t("success"),
          this.$t(this.label("declineSuccess")),
        );
        this.$router.push({ name: "preorders" });
      } catch (error) {
        console.error("Error declining preorder", error);
        this.alertToast(
          "danger",
          this.$t("Alert"),
          error.data?.message || error.message,
        );
      } finally {
        stopLoading();
      }
    },

    goBack() {
      this.$router.push({ name: "preorders" });
    },
    alertToast(type = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon: "CoffeeIcon",
      });
    },
    async getCompanyGuid(guid) {
      showLoading();
      await getCompany(guid)
        .then((resp) => {
          console.log("resp", resp);
          stopLoading();
          const company = _.get(resp, "data");
          let phoneFormat = company.phoneNumber.replace(/\s/g, "");
          this.whatsappPhone = phoneFormat;
        })
        .catch((error) => {
          stopLoading();
          console.log("error", error);
          this.alertToast(
            "danger",
            "Error",
            error.message,
            "AlertTriangleIcon",
          );
        });
    },
    onWhatsApp() {
      console.log("this.preorder", this.preorder);
      // if (!this.whatsappPhone) {
      //   this.alertToast(
      //     "warning",
      //     "Alerta",
      //     "No hay número de WhatsApp configurado"
      //   );
      //   return;
      // }

      // Crear mensaje con información de la preorden
      // const message =
      //   `Hola, un gusto saludarte soy la persona encargada de atender tu preorden #${this.preorder.idOrder}\n\n` +
      //   `Notas: ${this.preorder.notes || "Sin notas"}\n` +
      //   `Estado: ${this.preorder.nameStatus}\n` +
      //   `Fecha: ${this.dateFormat(this.preorder.date)}`;

      // // Codificar el mensaje para URL
      // const encodedMessage = encodeURIComponent(message);

      // Abrir WhatsApp con el mensaje
      // const whatsappUrl = `https://wa.me/${this.preorder.phoneClient}?text=${encodedMessage}`;
      const whatsappUrl = this.preorder.urlChatBoot;
      window.open(whatsappUrl, "_blank");
    },

    onSOFIA() {
      // Crear mensaje con información de la preorden
      // const message =
      //   `Hola, un gusto saludarte soy la persona encargada de atender tu preorden #${this.preorder.idOrder}\n\n` +
      //   `Notas: ${this.preorder.notes || "Sin notas"}\n` +
      //   `Estado: ${this.preorder.nameStatus}\n` +
      //   `Fecha: ${this.dateFormat(this.preorder.date)}`;

      // // Codificar el mensaje para URL
      // const encodedMessage = encodeURIComponent(message);

      // Abrir WhatsApp con el mensaje
      // const whatsappUrl = `https://wa.me/${this.preorder.phoneClient}?text=${encodedMessage}`;
      const whatsappUrl = this.preorder.urlChatSofia;
      window.open(whatsappUrl, "_blank");
    },

    dateFormat(date) {
      if (!date) return "-";
      const normalizedDate =
        typeof date === "string" ? date.trim().replace(" ", "T") : date;
      const utcDate =
        typeof normalizedDate === "string" &&
        !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedDate)
          ? `${normalizedDate}Z`
          : normalizedDate;
      const fecha = new Date(utcDate);
      if (Number.isNaN(fecha.getTime())) return "-";
      const dia = fecha.getDate().toString().padStart(2, "0");
      const mes = (fecha.getMonth() + 1).toString().padStart(2, "0");
      const anio = fecha.getFullYear();
      const hour24 = fecha.getHours();
      const horas = (hour24 % 12 || 12).toString().padStart(2, "0");
      const minutos = fecha.getMinutes().toString().padStart(2, "0");
      const period = hour24 >= 12 ? "PM" : "AM";
      return `${dia}/${mes}/${anio} ${horas}:${minutos} ${period}`;
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped>
.sofia-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  vertical-align: middle;
  cursor: pointer;
}

.sofia-action-button:focus-visible {
  outline: 2px solid #7367f0;
  outline-offset: 3px;
  border-radius: 3px;
}

.employee-loader {
  padding: 0.75rem;
  color: #b8b8b8;
  text-align: center;
}

.preorder-toolbar,
.preorder-section-card,
.decision-panel {
  border: 0;
  border-radius: 0.65rem;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.08);
}

.preorder-toolbar {
  overflow: hidden;
  border-left: 4px solid #7367f0;
}

.preorder-toolbar-body {
  padding: 1.4rem 1.5rem;
}

.preorder-toolbar-content,
.preorder-toolbar-main,
.preorder-toolbar-actions,
.section-heading,
.section-heading-icon,
.info-row,
.decision-panel,
.decision-copy,
.decision-icon,
.decision-actions {
  display: flex;
  align-items: center;
}

.preorder-toolbar-content {
  justify-content: space-between;
  gap: 1.5rem;
}

.preorder-toolbar-main {
  min-width: 0;
  gap: 1rem;
}

.preorder-toolbar-icon,
.section-heading-icon,
.decision-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 0.65rem;
}

.preorder-toolbar-icon {
  width: 3.25rem;
  height: 3.25rem;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.12);
}

.preorder-toolbar-eyebrow {
  display: block;
  margin-bottom: 0.15rem;
  color: #7367f0;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.preorder-toolbar-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1rem;
  color: inherit;
  font-size: 0.9rem;
  opacity: 0.75;
}

.preorder-toolbar-meta span + span::before {
  content: "•";
  margin-right: 1rem;
  color: #b9b9c3;
}

.preorder-toolbar-actions {
  flex: 0 0 auto;
  gap: 0.75rem;
}

.preorder-status-badge {
  padding: 0.55rem 0.85rem;
  font-size: 0.85rem;
}

.preorder-section-card {
  position: relative;
  height: calc(100% - 2rem);
  overflow: visible;
}

.assignment-section-card {
  z-index: 2;
}

.preorder-section-header {
  min-height: 5.4rem;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.35rem 1.5rem;
}

.preorder-section-body {
  padding: 0 1.5rem 1.5rem;
}

.section-heading {
  min-width: 0;
  gap: 0.85rem;
}

.section-heading-icon {
  width: 2.65rem;
  height: 2.65rem;
  flex-basis: 2.65rem;
}

.financial-icon {
  color: #7367f0;
  background: rgba(115, 103, 240, 0.12);
}

.assignment-icon {
  color: #ff9f43;
  background: rgba(255, 159, 67, 0.12);
}

.payment-icon {
  color: #00cfe8;
  background: rgba(0, 207, 232, 0.12);
}

.products-icon {
  color: #28c76f;
  background: rgba(40, 199, 111, 0.12);
}

.notes-icon {
  color: #ea5455;
  background: rgba(234, 84, 85, 0.12);
}

.history-icon {
  color: #82868b;
  background: rgba(130, 134, 139, 0.12);
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.info-row {
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 0;
  line-height: 1.45;
}

.info-row-icon {
  flex: 0 0 21px;
  margin-top: 0.15rem;
}

.info-row-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.15rem;
  overflow-wrap: anywhere;
}

.info-row-copy small {
  color: #b9b9c3;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.info-row-copy strong {
  font-weight: 500;
}

.shipping-info-row {
  padding: 0.85rem;
  border-radius: 0.5rem;
  background: rgba(115, 103, 240, 0.045);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.9rem 0;
}

.summary-row + .summary-row {
  border-top: 1px solid rgba(34, 41, 47, 0.08);
}

.summary-row--highlight {
  font-size: 1.05rem;
  font-weight: 600;
}

.financial-divider {
  height: 1px;
  margin: 0.35rem 0;
  background: rgba(34, 41, 47, 0.1);
}

.summary-context {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  color: #7367f0;
  border-radius: 0.5rem;
  background: rgba(115, 103, 240, 0.07);
  font-size: 0.85rem;
}

.notes-content {
  min-height: 5rem;
  line-height: 1.6;
  white-space: pre-line;
}

#preordersform-table {
  margin-bottom: 0;
}

.decision-column {
  margin-top: 0.25rem;
}

.decision-panel {
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1.35rem 1.5rem;
  border-left: 4px solid #7367f0;
  background: rgba(115, 103, 240, 0.035);
}

.decision-copy {
  min-width: 0;
  gap: 0.85rem;
}

.decision-icon {
  width: 2.75rem;
  height: 2.75rem;
  flex-basis: 2.75rem;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.12);
}

.decision-actions {
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
}

@media (max-width: 991.98px) {
  .decision-panel {
    align-items: flex-start;
    flex-direction: column;
  }

  .decision-actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 767.98px) {
  .preorder-toolbar-content,
  .preorder-toolbar-actions,
  .decision-actions {
    width: 100%;
  }

  .preorder-toolbar-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .preorder-toolbar-actions {
    justify-content: space-between;
  }

  .preorder-toolbar-meta {
    flex-direction: column;
    gap: 0.15rem;
  }

  .preorder-toolbar-meta span + span::before {
    display: none;
  }

  .preorder-section-header {
    padding: 1.25rem 1rem;
  }

  .preorder-section-body {
    padding: 0 1rem 1rem;
  }

  .summary-row {
    flex-direction: column;
    gap: 0.25rem;
  }

  .decision-actions {
    flex-direction: column;
  }

  .decision-actions .btn {
    width: 100%;
  }
}
</style>
